/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Textbox from '../Textbox/Textbox';
import Numericbox from '../Numericbox/Numericbox';
import Combobox from '../Combobox/Combobox';
import Label from '../Label/Label';
import Radio from '../Radio/Radio';
import Checkbox from '../Checkbox/Checkbox';
import Button from '../Button/Button';
import Service from '../Util/Service';
import $ from '../Util/Util';
import AddIcon from './baseline_add_black_24dp.png';
import RemoveIcon from './baseline_delete_black_24dp.png';
import './Attributeview.css';

/**
 * Represents the attribute view component.
 */

export default class Attributeview extends Component {
   
    /**
     * Properties.
     */

    static propTypes = {
        context:                PropTypes.object.isRequired,
        attribute:              PropTypes.object,
        onClose:                PropTypes.func,
        onLoading:              PropTypes.func.isRequired,
        onReload:               PropTypes.func,
    }

    /**
     * Default state.
     */

    state = {
        types: [
            { id: 'integer', name: this.props.context.locale.attribute.type.integer, defaultvalue: 0, minvalue: 0, maxvalue: 99999999 },
            { id: 'float', name: this.props.context.locale.attribute.type.float, defaultvalue: 0, minvalue: 0, maxvalue: 99999999 },
            { id: 'text', name: this.props.context.locale.attribute.type.text, size: 40, defaultvalue: '' },
            { id: 'multilinetext', name: this.props.context.locale.attribute.type.multilinetext, size: 1000, defaultvalue: '' },
            { id: 'date', name: this.props.context.locale.attribute.type.date, defaultvalue: 'today' },
            { id: 'radio', name: this.props.context.locale.attribute.type.radio },
            { id: 'checkbox', name: this.props.context.locale.attribute.type.checkbox },
            { id: 'externallink', name: this.props.context.locale.attribute.type.externallink },
            { id: 'picture', name: this.props.context.locale.attribute.type.picture },
            { id: 'file', name: this.props.context.locale.attribute.type.file },
            { id: 'user', name: this.props.context.locale.attribute.type.user },
            { id: 'comment', name: this.props.context.locale.attribute.type.comment, size: 1000, defaultvalue: '' }
        ],
        newattribute:           true,
        type:                   null,
        radio:                  [],
        checkbox:               [],
        datedefaultvalue:       'empty'
    }

    /**
     * Event that fires when the user selects an attribute type from the combo.
     * 
     * @param   {Object}    type    selected attribute type
     */

    _onTypeSelected = (type) => {
        this._refreshAttribute(type);
    }

    /**
     * Refreshes the data of the attribute.
     * 
     * @param   {Object}    selectedType        selected type (Optional)
     */

    _refreshAttribute = (selectedType) => {

        // Is it a new attribute?

        let newAttribute = this.props.attribute ? false : true;

        // Name and placeholder of the attribute

        if (!selectedType) {
            this.name.setValue(newAttribute ? this.props.context.locale.attribute.unnamedattr : this.props.attribute.name);
            this.placeholder.setValue(newAttribute ? '' : this.props.attribute.placeholder || '');
        }

        // Type of the attribute

        let type = newAttribute ? (selectedType || this.state.types[0]).id : this.props.attribute.type;

        for (let i = 0; i < this.state.types.length; i++) {
            if (this.state.types[i].id === type) {
                type = this.state.types[i];
                break;
            }
        }

        // Refreshing state

        this.setState({ newattribute: newAttribute, type: type }, () => {
            this.type.setValue(type.id);

            // Size of the attribute

            if (type.size) {
                this.size.setValue(newAttribute ? type.size : this.props.attribute.size);
            }

            // Default value of the attribute

            if (type.defaultvalue !== undefined) {
                let defaultvalue = newAttribute ? type.defaultvalue : this.props.attribute.defaultvalue;

                if (['integer', 'float'].indexOf(type.id) >= 0) {
                    defaultvalue =  defaultvalue || 0;
                } else if (type.id === 'date') {
                    defaultvalue =  defaultvalue || 'empty';
                    this.setState({ datedefaultvalue: defaultvalue });
                } else {
                    defaultvalue = defaultvalue || '';
                }

                this.defaultvalue.setValue(defaultvalue);
            }

            // Minimal value of the attribute

            if (type.minvalue !== undefined) {
                this.minvalue.setValue(newAttribute ? type.minvalue : (this.props.attribute.minvalue || type.minvalue));
            }

            // Maximal value of the attribute

            if (type.maxvalue !== undefined) {
                this.maxvalue.setValue(newAttribute ? type.maxvalue : (this.props.attribute.maxvalue || type.maxvalue));
            }

            // Refreshing radio values of the attribute

            if (type.id === 'radio') {
                let radio = [];

                if (newAttribute) {
                    radio = [{ 
                        text:       this.props.context.locale.attribute.options.option1, 
                        checked:    true 
                    }, { 
                        text:       this.props.context.locale.attribute.options.option2, 
                        checked:    false 
                    }];
                } else {
                    this.props.attribute.values.forEach((item) => {
                        radio.push({ text: item, checked: item === this.props.attribute.defaultvalue });
                    });

                    let hasChecked = false;

                    for (let i = 0; i < radio.length; i++) {
                        if (radio[i].checked) {
                            hasChecked = true;
                            break;
                        }
                    }

                    if (!hasChecked) {
                        radio[0].checked = true;
                    }
                }

                this.setState({ radio: radio }, () => {
                    radio.forEach((item, index) => {
                        this['radiotext' + index].setValue(item.text);
                    });
                });
            }

            // Refreshing checbox values of the attribute

            if (type.id === 'checkbox') {
                let checkbox = [];

                if (newAttribute) {
                    checkbox = [{ 
                        text:       this.props.context.locale.attribute.options.option1, 
                        checked:    false
                    }, {
                        text:       this.props.context.locale.attribute.options.option2,
                        checked:    false
                    }];
                } else {
                    this.props.attribute.values.forEach((item) => {
                        checkbox.push({ 
                            text:       item, 
                            checked:    (this.props.attribute.defaultvalue || []).indexOf(item) >= 0 
                        });
                    });
                }

                this.setState({ checkbox: checkbox }, () => {
                    checkbox.forEach((item, index) => {
                        this['checkboxtext' + index].setValue(item.text);
                    });
                });
            }
        });
    }

    /**
     * Event that fires when the user wants to delete the attribute.
     */

    _onDelete = () => {
        if (window.confirm(this.props.context.locale.attribute.confirmdelete)) {

            // Deleting the attribute

            Service.deleteAttribute(this.props.attribute.id).then(() => {

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Showing the message

                $.fireEvent('snackbar', { message: this.props.context.locale.attribute.deleted });

                // Reloading the attributes

                if (!this.props.context.environment.browser.info.mobile && this.props.onReload) {
                    this.props.onReload();
                }

                // Closing the dialog/screen

                setTimeout(() => {
                    this.props.onClose(true);
                }, this.props.context.environment.browser.info.mobile ? 200: 10);
            }).catch((error) => {

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
            });
        }
    }

    /**
     * Event that fires when the user wants to save the attribute.
     */

    _onSave = () => {
        
        // Does the attribute data valid?

        if (!this.name.validate()) {
            return;
        }

        // Checking size

        if (this.state.type.size !== undefined) {
            if (this.size.getValue() === 0) {
                this.size.setError(this.props.context.locale.attribute.validation.sizecheck);
                return;
            } else {
                if (!this.size.validate()) {
                    return;
                }
            }
        }

        // Checking default value

        if (this.state.type.defaultvalue !== undefined) {
            if (!this.defaultvalue.validate()) {
                return;
            }
        }

        // Checking minimum value

        if (this.state.type.minvalue !== undefined) {
            if (!this.minvalue.validate()) {
                return;
            }
        }

        // Checking maximum value

        if (this.state.type.maxvalue !== undefined) {
            if (!this.maxvalue.validate()) {
                return;
            }
        }

        // Checking empty radio labels

        if (this.state.type.id === 'radio') {
            for (let i = 0; i < this.state.radio.length; i++) {
                if (!this['radiotext' + i].validate()) {
                    return;
                }
            }
        }

        // Checking empty chekbox labels

        if (this.state.type.id === 'checkbox') {
            for (let i = 0; i < this.state.checkbox.length; i++) {
                if (!this['checkboxtext' + i].validate()) {
                    return;
                }
            }
        }

        // Showing progress indicator

        this.props.onLoading(true);

        // Getting the attribute

        let attribute = {
            name:           this.name.getValue(),
            placeholder:    this.placeholder.getValue(),
            type:           this.state.type.id
        };

        // Storing size

        if (this.state.type.size !== undefined) {
            attribute.size = this.size.getValue();
        }

        // Storing default value

        if (this.state.type.defaultvalue !== undefined) {
            if (this.state.type.id === 'date') {
                attribute.defaultvalue = this.state.datedefaultvalue;
            } else {
                attribute.defaultvalue = this.defaultvalue.getValue();
            }
        }

        // Storing minimum value

        if (this.state.type.minvalue !== undefined) {
            attribute.minvalue = this.minvalue.getValue();
        }

        // Storing maximum value

        if (this.state.type.maxvalue !== undefined) {
            attribute.maxvalue = this.maxvalue.getValue();
        }

        // Storing radio labels and the default value

        if (this.state.type.id === 'radio') {
            attribute.values = [];

            this.state.radio.forEach((item, index) => {
                let text = this['radiotext' + index].getValue();

                if (this['radio' + index].getValue()) {
                    attribute.defaultvalue = text;
                }

                attribute.values.push(text);
            });
        }

        // Storing checkbox labels and the default values

        if (this.state.type.id === 'checkbox') {
            attribute.values = [];
            attribute.defaultvalue = [];

            this.state.checkbox.forEach((item, index) => {
                let text = this['checkboxtext' + index].getValue();

                if (this['checkbox' + index].getValue()) {
                    attribute.defaultvalue.push(text);
                }

                attribute.values.push(text);
            });
        }

        // Storing the ID of the attribute if it is not a new attribute

        if (!this.state.newattribute) {
            attribute.id = this.props.attribute.id;
        }

        // Saving the attribute

        Service.saveAttribute(attribute).then(() => {

            // Hiding the progress indicator

            this.props.onLoading(false);

            // Showing the message

            $.fireEvent('snackbar', { message: this.props.context.locale.attribute.savedmessage });

            // Reloading the attributes

            if (!this.props.context.environment.browser.info.mobile && this.props.onReload) {
                this.props.onReload();
            }

            // Closing the dialog/screen

            setTimeout(() => {
                this.props.onClose(true);
            }, this.props.context.environment.browser.info.mobile ? 200: 10);
        }).catch((error) => {

            // Hiding the progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /**
     * Adds a new radio button.
     */

    _addRadio = () => {

        // Creating the radio button

        let radio = this.state.radio;
        radio.push({
            text:       `${this.props.context.locale.attribute.options.option} ${radio.length + 1}`,
            checked:    false
        });

        // Refreshing the radio

        this.setState({ radio: radio }, () => {
            this['radiotext' + (radio.length - 1)].setValue(radio[radio.length - 1].text);
        });
    }

    /**
     * Removes the radio button at the specified index.
     * 
     * @param   {Number}    index   index of the radio button
     */

    _removeRadio = (index) => {

        // Removing the radio

        let radio = this.state.radio;
        radio.splice(index, 1);

        // Is there any selected radio button?

        let hasChecked = false;

        for (let i = 0; i < radio.length; i++) {
            if (radio[i].checked) {
                hasChecked = true;
                break;
            }
        }

        // Selecting the first radio button if there was no any

        if (!hasChecked) {
            radio[0].checked = true;
        }

        // Refreshing the radio

        this.setState({ radio: radio }, () => {
            radio.forEach((item, i) => {
                this['radio' + i].setValue(item.checked);
                this['radiotext' + i].setValue(item.text);
            });
        });
    }

    /**
     * Event that fires when a radio button selected.
     * 
     * @param   {String}    group   group of the radios
     * @param   {Number}    index   index of the selected radio
     */

    _onRadioChecked = (group, index) => {

        // Getting the radio

        let radio = this.state.radio;

        // Setting the selection status of the radios

        radio.forEach((item, i) => {
            radio[i].checked = (i === index);
        });

        // Refreshing the radio

        this.setState({ radio: radio });
    }

    /**
     * Adds a new checkbox button.
     */

    _addCheckbox = () => {

        // Creating the checkbox button

        let checkbox = this.state.checkbox;
        checkbox.push({ 
            text:       `${this.props.context.locale.attribute.options.option} ${checkbox.length + 1}`, 
            checked:    false
        });

        // Refreshing the checkbox

        this.setState({ checkbox: checkbox }, () => {
            this['checkboxtext' + (checkbox.length - 1)].setValue(checkbox[checkbox.length - 1].text);
        });
    }

    /**
     * Removes the checkbox button at the specified index.
     * 
     * @param   {Number}    index   index of the checkbox button
     */

    _removeCheckbox = (index) => {

        // Removing the checkbox

        let checkbox = this.state.checkbox;
        checkbox.splice(index, 1);

        // Refreshing the checkbox

        this.setState({ checkbox: checkbox }, () => {
            checkbox.forEach((item, i) => {
                this['checkbox' + i].setValue(item.checked);
                this['checkboxtext' + i].setValue(item.text);
            });
        });
    }

    /**
     * Event that fires when a checkbox button selected.
     * 
     * @param   {Boolean}   value   state of the checkbox
     * @param   {Number}    index   index of the selected checkbox
     */

    _onCheckboxChecked = (value, index) => {

        // Getting the checkbox

        let checkbox = this.state.checkbox;

        // Setting the checked state of the checkbox

        checkbox[index].checked = value;

        // Refreshing the checkbox

        this.setState({ checkbox: checkbox });
    }

    /**
     * Sets the default value of the datebox.
     * 
     * @param   {Object}    value   selected default value
     */

    _setDateDefaultValue = (value) => {

        // Setting the default value of the datebox

        this.setState({ datedefaultvalue: value.id });
    }

    /** 
     * Event that fires when the component mounted.
     */

    componentDidMount() {
        this._refreshAttribute();
    }

    /** 
     * Attribute view renderer.
     */

    render() {
        return (
            <div className="attributeview">

                {/* Dialog title */}

                {!this.props.context.environment.browser.info.mobile &&
                    <div className="attributeview-title">{this.state.newattribute ? this.props.context.locale.attribute.title.newattribute : this.props.context.locale.attribute.title.modifyattribute}</div>
                }

                {/* Dialog body */}

                <div className="attributeview-attribs vertical"style={{ height: (this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.usefullheight - 90) : 310) + 'px' }}>

                    {/* Name */}

                    <Textbox ref={c => {this.name = c;}} tooltip={this.props.context.locale.attribute.field.name.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 62) : 452} maxlength={40} label={this.props.context.locale.attribute.field.name.label} labelwidth={100} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} isrequired requiredmessage={this.props.context.locale.attribute.field.required} validateonblur comment={this.props.context.locale.attribute.field.name.comment} placeholder={this.props.context.locale.attribute.field.name.placeholder} />
                    
                    {/* Type */}

                    <Combobox ref={c => {this.type = c;}} disabled={!this.state.newattribute} tooltip={this.props.context.locale.attribute.field.type.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 64) : 240} label={this.props.context.locale.attribute.field.type.label} labelwidth={100} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} comment={this.props.context.locale.attribute.field.type.comment} idattr="id" nameattr="name" source={this.state.types} onSelect={this._onTypeSelected} />

                    {/* Placeholder */}

                    <Textbox ref={c => {this.placeholder = c;}} tooltip={this.props.context.locale.attribute.field.placeholder.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 62) : 452} maxlength={100} label={this.props.context.locale.attribute.field.placeholder.label} labelwidth={100} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} requiredmessage={this.props.context.locale.attribute.field.required} validateonblur comment={this.props.context.locale.attribute.field.placeholder.comment} placeholder={this.props.context.locale.attribute.field.placeholder.placeholder} />

                    {/* Size */}

                    { this.state.type && this.state.type.size &&
                        <Numericbox ref={c => {this.size = c;}} tooltip={this.props.context.locale.attribute.field.size.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 62) : 30} maxlength={4} label={this.props.context.locale.attribute.field.size.label} labelwidth={100} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} isrequired requiredmessage={this.props.context.locale.attribute.field.required} validateonblur comment={this.props.context.locale.attribute.field.size.comment} placeholder={this.props.context.locale.attribute.field.size.placeholder} />
                    }

                    {/* Default value for numeric box */}

                    { this.state.type && (['integer', 'float'].indexOf(this.state.type.id) >= 0) && this.state.type.defaultvalue !== undefined &&
                        <Numericbox ref={c => {this.defaultvalue = c;}} tooltip={this.props.context.locale.attribute.field.defaultvalue.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 62) : 76} maxlength={10} label={this.props.context.locale.attribute.field.defaultvalue.label} labelwidth={100} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} isfloat={this.state.type.id === 'float'} isnegative validateonblur comment={this.props.context.locale.attribute.field.defaultvalue.comment} placeholder={this.props.context.locale.attribute.field.defaultvalue.placeholder} />
                    }

                    {/* Default value for textbox, multilinetext and comment */}

                    { this.state.type && (['text', 'multilinetext', 'comment'].indexOf(this.state.type.id) >= 0) && this.state.type.defaultvalue !== undefined &&
                        <Textbox ref={c => {this.defaultvalue = c;}} tooltip={this.props.context.locale.attribute.field.defaultvalue.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 62) : 76} maxlength={10} label={this.props.context.locale.attribute.field.defaultvalue.label} labelwidth={100} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} validateonblur comment={this.props.context.locale.attribute.field.defaultvalue.comment} placeholder={this.props.context.locale.attribute.field.defaultvalue.placeholder} />
                    }

                    {/* Default value for datebox */}

                    { this.state.type && this.state.type.id === 'date' && this.state.type.defaultvalue !== undefined &&
                        <Combobox ref={c => {this.defaultvalue = c;}} tooltip={this.props.context.locale.attribute.field.defaultvalue.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 64) : 120} label={this.props.context.locale.attribute.field.defaultvalue.label} labelwidth={100} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} comment={this.props.context.locale.attribute.field.defaultvalue.comment} idattr="id" nameattr="name" source={[{ id: 'empty', name: this.props.context.locale.attribute.field.datevalue.empty}, {id: 'today', name: this.props.context.locale.attribute.field.datevalue.today}]} onSelect={this._setDateDefaultValue} />
                    }

                    {/* Minimum value */}

                    { this.state.type && (this.state.type.id === 'integer' || this.state.type.id === 'float') && this.state.type.minvalue !== undefined &&
                        <Numericbox ref={c => {this.minvalue = c;}} tooltip={this.props.context.locale.attribute.field.minimumvalue.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 62) : 76} maxlength={10} label={this.props.context.locale.attribute.field.minimumvalue.label} labelwidth={100} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} isfloat={this.state.type.id === 'float'} isnegative validateonblur comment={this.props.context.locale.attribute.field.minimumvalue.comment} placeholder={this.props.context.locale.attribute.field.minimumvalue.placeholder} />
                    }

                    {/* Maximum value */}

                    { this.state.type && (this.state.type.id === 'integer' || this.state.type.id === 'float') && this.state.type.maxvalue !== undefined &&
                        <Numericbox ref={c => {this.maxvalue = c;}} tooltip={this.props.context.locale.attribute.field.maximumvalue.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 62) : 76} maxlength={10} label={this.props.context.locale.attribute.field.maximumvalue.label} labelwidth={100} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} isfloat={this.state.type.id === 'float'} isnegative validateonblur comment={this.props.context.locale.attribute.field.maximumvalue.comment} placeholder={this.props.context.locale.attribute.field.maximumvalue.placeholder} />
                    }

                    {/* Radio buttons */}

                    { this.state.type && this.state.type.id === 'radio' && <div>

                        { this.props.context.environment.browser.info.mobile && <Label text={this.props.context.locale.attribute.field.radio.label} width={100} bottomgap={4} isrequired /> }

                        { this.state.radio.map((item, index) =>
                            <div key={index} className="horizontal" style={{ marginLeft: (index === 0 || this.props.context.environment.browser.info.mobile ? 0 : 100) + 'px' }}>
                                { !this.props.context.environment.browser.info.mobile && index === 0 && <Label text={this.props.context.locale.attribute.field.radio.label} width={100} bottomgap={24} isrequired /> }
                                <Radio ref={c => {this['radio' + index] = c;}} checked={item.checked} holder={this} group="radio" index={index} onChecked={this._onRadioChecked} />
                                <Textbox ref={c => {this['radiotext' + index] = c;}} width={this.props.context.environment.browser.info.mobile ? 180 : 202} comment={(index === this.state.radio.length - 1) ? this.props.context.locale.attribute.field.radio.placeholder : undefined} placeholder={this.props.context.locale.attribute.field.radio.placeholder} isrequired requiredmessage={this.props.context.locale.attribute.field.required} validateonblur maxlength={40} />
                                { index > 1 && <img alt="Remove radio" src={RemoveIcon} className="attributeview-addremove-icon" title={this.props.context.locale.attribute.field.radio.removetooltip} onClick={this._removeRadio.bind(this, index)} /> }
                                { (index === this.state.radio.length - 1) && <img alt="Add radio" src={AddIcon} className="attributeview-addremove-icon" title={this.props.context.locale.attribute.field.radio.addtooltip} onClick={this._addRadio} /> }
                            </div>
                        )}
                    </div>}

                    {/* Checkbox buttons */}

                    { this.state.type && this.state.type.id === 'checkbox' && <div>

                        { this.props.context.environment.browser.info.mobile && <Label text={this.props.context.locale.attribute.field.checkbox.label} width={100} bottomgap={4} isrequired /> }

                        { this.state.checkbox.map((item, index) =>
                            <div key={index} className="horizontal" style={{ marginLeft: (index === 0 || this.props.context.environment.browser.info.mobile ? 0 : 100) + 'px' }}>
                                { !this.props.context.environment.browser.info.mobile && index === 0 && <Label text="Opciók:" width={100} bottomgap={24} isrequired /> }
                                <Checkbox ref={c => {this['checkbox' + index] = c;}} rightgap={8} checked={item.checked} index={index} onChecked={this._onCheckboxChecked} />
                                <Textbox ref={c => {this['checkboxtext' + index] = c;}} width={this.props.context.environment.browser.info.mobile ? 180 : 202} comment={(index === this.state.checkbox.length - 1) ? this.props.context.locale.attribute.field.checkbox.comment : undefined} placeholder={this.props.context.locale.attribute.field.checkbox.placeholder} isrequired requiredmessage={this.props.context.locale.attribute.field.required} validateonblur maxlength={40} />
                                { index > 1 && <img alt="Remove checkbox" src={RemoveIcon} className="attributeview-addremove-icon" title={this.props.context.locale.attribute.field.checkbox.removetooltip} onClick={this._removeCheckbox.bind(this, index)} /> }
                                { (index === this.state.checkbox.length - 1) && <img alt="Add checkbox" src={AddIcon} className="attributeview-addremove-icon" title={this.props.context.locale.attribute.field.checkbox.addtooltip} onClick={this._addCheckbox} /> }
                            </div>
                        )}
                    </div>}
                </div>

                {/* Buttons */}

                <div className="attributeview-buttons horizontal" style={{ marginBottom: (this.props.context.environment.browser.info.mobile ? 38 : 10) + 'px'}}>

                    {/* Delete button */}

                    { !this.state.newattribute && <Button label={this.props.context.locale.attribute.buttons.delete.label} tooltip={this.props.context.locale.attribute.buttons.delete.tooltip} width={60} onTouch={this._onDelete} /> }

                    {/* Spacer */}

                    <div className="flex"></div>

                    {/* Cancel button */}

                    {!this.props.context.environment.browser.info.mobile &&
                        <Button label={this.props.context.locale.attribute.buttons.cancel.label} tooltip={this.props.context.locale.attribute.buttons.cancel.tooltip} rightgap={8} width={60} onTouch={this.props.onClose.bind(this, false)} />
                    }

                    {/* Save button */}

                    <Button label={this.props.context.locale.attribute.buttons.save.label} tooltip={this.props.context.locale.attribute.buttons.cancel.tooltip} width={60} onTouch={this._onSave} />
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Attributeview/Attributeview.js